import { useMutation, useQuery } from '@tanstack/react-query';
import { cloneDeep } from 'lodash';

declare global {
  interface Window {
    structuredClone<T>(value: T): T;
  }
  function structuredClone<T>(value: T): T;
}
import backendService from '../services/backendService';
import BackendService, {
  ConfirmationState,
} from '../services/backendService/backendService';
import useRouterMatch from './useRouterMatch';
export const accessBadgeCacheKey = 'accessBadge';

export enum LicenseCoarseLevels {
  enterprise = 'Enterprise',
  premium = 'Premium',
  freemium = 'Freemium',
}

export enum LicenseFineLevels {
  trial = 'Trial',
  full = 'Full',
}

export enum OrganizationType {
  School = 'School',
  District = 'District',
  Other = 'Other',
}

export enum OrganizationSource {
  MCH = 'MCH',
  Curipod = 'Curipod',
  User = 'User',
}

export type SimplifiedUser = {
  id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email: string;
  preferredLocale?: string;
  profileInfo?: {
    role?: string;
    subjects?: string[];
    grades?: string[];
    intendedUses?: string[];
    otherIntendedUse?: string;
    signUpOrigin?: string;
    otherSignUpOrigin?: string;
  };
  createdAt: number;
  onboardingCompletedAt?: number;
};

export type SimplifiedLicense = {
  id?: string;
  source?:
    | 'EnterpriseOnboardingStarted'
    | 'PaidPilot'
    | 'FreePilot'
    | 'AmbassadorAccess'
    | 'OneOffExtension'
    | 'SignUp'
    | 'PremiumDiscontinued';
  coarseLevel: LicenseCoarseLevels;
  fineLevel: LicenseFineLevels;
  aiAccessBlocked: boolean;
  expiresAt?: number;
  createdAt: number;
};

export type SimplifiedOrganization = {
  id: string;
  type: OrganizationType;
  source: OrganizationSource;
  name: string;
  primaryZipCode?: string;
  secondaryZipCode?: string;
  city?: string;
  region?: string;
  country?: string;
  ssoProvider?: 'Google' | 'Clever' | 'Classlink' | 'Feide';
  parent?: SimplifiedOrganization;
  createdAt: number;
  reconfirmAt: Date | null;
};

// Kept for legacy purposes. Will be deprecated in the near future.
export type LegacyTenant = {
  tenantId: string;
  tenantName: string;
  organisationId?: string | null;
  isEnterprise?: boolean | null; // Deprecated - used by pods enterprise - dont migrate data before pod is 100% shutoff
  districtId?: string | null;
  districtName?: string | null;
  state?: string | null;
  country?: string | null;
  createdAt?: number | null;
  createdBy?: string | null;
  isPremium?: boolean | null; // Deprecated - used by pods - dont migrate data before pod is 100% shutoff
  isCuripodPremium?: boolean | null;
  isCuripodEnterprise?: boolean | null;
  curipodTrialExpirationDate?: number;
  curipodTrialReason?: string;
  curipodEnterpriseExpirationDate?: number;
};

export type RawUserAccessBadge = {
  role: 'Admin' | 'Member';
  user: SimplifiedUser;
  license?: SimplifiedLicense;
  organization?: SimplifiedOrganization;
  otherOrganizations?: SimplifiedOrganization[];
  tenant: LegacyTenant;
  canPurchaseIndividualPremium: boolean;
};

function deduplicateOrganizations(
  orgs: SimplifiedOrganization[] | undefined,
  excludeId?: string,
): SimplifiedOrganization[] | undefined {
  if (!orgs) return undefined;
  const seen = new Set<string>();
  if (excludeId) seen.add(excludeId);
  return orgs.filter(org => {
    if (seen.has(org.id)) return false;
    seen.add(org.id);
    return true;
  });
}

export class UserAccessBadge {
  readonly role: 'Admin' | 'Member';
  readonly user: Readonly<SimplifiedUser>;
  readonly license: Readonly<SimplifiedLicense>;
  readonly organization?: Readonly<SimplifiedOrganization>;
  readonly otherOrganizations?: Readonly<SimplifiedOrganization[]>;
  readonly tenant: Readonly<LegacyTenant>;
  readonly canPurchaseIndividualPremium: boolean;

  constructor(
    role: 'Admin' | 'Member',
    user: SimplifiedUser,
    license: SimplifiedLicense | undefined,
    organization: SimplifiedOrganization | undefined,
    otherOrganizations: SimplifiedOrganization[] | undefined,
    tenant: LegacyTenant,
    canPurchaseIndividualPremium: boolean,
  ) {
    this.role = role;
    this.user = Object.freeze(cloneDeep(user));
    this.license = license
      ? Object.freeze(cloneDeep(license))
      : {
          coarseLevel: LicenseCoarseLevels.freemium,
          fineLevel: LicenseFineLevels.full,
          aiAccessBlocked: false,
          createdAt: new Date().getTime(),
        };
    this.organization = Object.freeze(cloneDeep(organization));
    this.otherOrganizations = Object.freeze(
      cloneDeep(deduplicateOrganizations(otherOrganizations, organization?.id)),
    );
    this.tenant = Object.freeze(cloneDeep(tenant));
    this.canPurchaseIndividualPremium = canPurchaseIndividualPremium;
    Object.freeze(this);
  }
}

function useAccessBadge() {
  const { tenantId: activeTenantId } = useRouterMatch();
  const { data, isFetching, isFetched, refetch: refetchAccessBadge } = useQuery({
    queryKey: [accessBadgeCacheKey, activeTenantId],

    queryFn: _ => {
      if (!activeTenantId) return undefined;
      return backendService.instance.users.getAccessBadge(activeTenantId);
    },

    staleTime: 1000 * 60 * 60,
    enabled: !!activeTenantId
  });

  const { mutate, isSuccess, isPending: isLoading } = useMutation({
    mutationFn: async ({
      schoolId,
      schoolCountry,
    }: {
      schoolId?: number;
      schoolCountry?: string;
      onSuccessFunction?: () => void;
    }) => {
      return backendService.instance.users.updateSchoolInfo({
        activeTenantId,
        schoolId,
        schoolCountry,
      });
    },

    onSuccess: (_, { onSuccessFunction }) => {
      if (onSuccessFunction) onSuccessFunction();
    }
  });

  const {
    mutate: setAssignmentConfirmation,
    isSuccess: isConfirmAssignmentSuccess,
    isPending: isConfirmAssignmentLoading,
  } = useMutation({
    mutationFn: async ({
      confirmationState,
      durationDays,
    }: {
      confirmationState: ConfirmationState;
      durationDays?: number;
      onSuccessFunction?: () => void;
    }) => {
      return backendService.instance.users.setAssignmentConfirmation(
        confirmationState,
        durationDays,
      );
    },

    onSuccess: (_, { onSuccessFunction }) => {
      if (onSuccessFunction) onSuccessFunction();
    }
  });

  const {
    mutate: reselectOrganization,
    isSuccess: isReselectOrganizationSuccess,
    isPending: isReselectOrganizationLoading,
  } = useMutation({
    mutationFn: async (params: {
      newOrgId?: string;
      customOrgName?: string;
      city?: string;
      region?: string;
      country?: string;
      primaryZipCode?: string;
      organizationType?: OrganizationType;
      onSuccessFunction?: () => void;
    }) => {
      const { onSuccessFunction: _onSuccessFunction, ...orgParams } = params;
      return backendService.instance.organizations.reselectOrganization(orgParams);
    },

    onSuccess: (_, { onSuccessFunction }) => {
      if (onSuccessFunction) onSuccessFunction();
    }
  });

  const { mutate: switchOrganization } = useMutation({
    mutationFn: (organizationId: string) =>
      BackendService.instance.switchOrganization(organizationId),

    onSuccess: () => {
      window.open(`${window.location.origin}`, '_self');
    }
  });

  return {
    accessBadge: data,
    isFetching,
    isFetched,
    refetchAccessBadge,
    // These are for the old school info update
    updateSchoolInfo: mutate,
    isUpdatingSchoolInfo: isLoading,
    isSuccess: isSuccess,
    // These are for the new assignment confirmation
    setAssignmentConfirmation,
    isConfirmAssignmentSuccess,
    isConfirmAssignmentLoading,
    // These are for the new organization reselection
    reselectOrganization,
    isReselectOrganizationSuccess,
    isReselectOrganizationLoading,
    switchOrganization,
  };
}

export default useAccessBadge;
